
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { google } from 'google-maps';
import { useFieldStore } from '../stores/fieldStore';
import { Point } from '../models/Point';
import { FieldOperationsModel } from '../models/FieldOperationsModel';
import { RenderHelper } from '../services/RenderHelper';

@Component
export default class FlightpathRenderPolygon extends Vue {
  @Prop() googleApi!: google;
  @Prop() map!: google.maps.Map<HTMLDivElement>;

  public fieldStore = useFieldStore();
  polygon!: google.maps.Polygon;
  polygonClickListenerHandle: google.maps.MapsEventListener | null = null;
  polygonDragListenerHandle: google.maps.MapsEventListener | null = null;
  mapClickListenerHandle: google.maps.MapsEventListener | null = null;

  async mounted() {
    this.addMapClickListener();
  }

  destroyed() {
    this.destroyResources();
  }

  deactivated() {
    this.destroyResources();
  }

  private destroyResources() {
    if (this.polygonClickListenerHandle != null) {
      this.polygonClickListenerHandle.remove();
    }
    if (this.polygonDragListenerHandle != null) {
      this.polygonDragListenerHandle.remove();
    }
    if (this.mapClickListenerHandle != null) {
      this.mapClickListenerHandle.remove();
    }
  }

  createNewPolygon(boundary: Point[]) {
    this.polygon = new this.googleApi.maps.Polygon(RenderHelper.getFieldPolygonOptionsFromPoint(boundary));
    this.polygon.setMap(this.map);
    this.polygon.setEditable(this.fieldStore.editFieldMode);

    this.addPolygonClickListener();
    this.addPolygonDragListener();
  }

  @Watch('fieldStore.field')
  onSelectedPathChange(field: FieldOperationsModel): void {
    if (this.polygon) this.polygon.setMap(null);
    if (!field) return;
    this.createNewPolygon(field.BoundaryPoints);
    if (this.polygon.getPath().getLength() != 0) {
      this.map.fitBounds(RenderHelper.getBounds(this.polygon.getPath()));
    }
    this.fieldStore.setSelectedFieldArea(this.findFieldArea());
    this.fieldStore.updateCurrentPolygon([
      this.polygon
        .getPath()
        .getArray()
        .map((o) => [o.lng(), o.lat()])
    ]);
  }

  @Watch('fieldStore.editMode')
  onEditableChange(): void {
    if (this.polygon) {
      this.polygon.setEditable(this.fieldStore.editFieldMode);
    }
  }

  @Watch('fieldStore.revertBoundary')
  onRevertBoundaryChange(): void {
    this.polygon.setMap(null);
    this.createNewPolygon(this.fieldStore.selectedField?.BoundaryPoints as Point[]);
    this.fieldStore.setSelectedFieldArea(this.findFieldArea());
  }

  @Watch('fieldStore.clearFieldMap')
  onClearMapChange(): void {
    if (this.polygon) {
      this.polygon.setMap(null);
    }
  }

  @Watch('fieldStore.addMode')
  onAddPathChange(value: boolean): void {
    if (value) {
      if (this.polygon) {
        this.polygon.setPath([]);
        this.polygon.setMap(this.map);
        this.fieldStore.updateCurrentPolygon([
          this.polygon
            .getPath()
            .getArray()
            .map((o) => [o.lng(), o.lat()])
        ]);
      }
    }
  }

  addPolygonClickListener() {
    if (!this.polygon) return;

    if (this.polygonClickListenerHandle != null) {
      this.polygonClickListenerHandle.remove();
    }

    this.polygonClickListenerHandle = this.polygon.addListener('click', (e) => {
      if (e.vertex != undefined) {
        this.deletePointFromPath(e.vertex);
      } else {
        if (this.fieldStore.addFieldMode) this.addPointToPath(e);
      }
    });
  }

  deletePointFromPath(vertexClicked: number) {
    if (!this.fieldStore.editFieldMode) return;

    const newPath = this.polygon.getPath();
    if (newPath.getLength() <= 3) return;

    newPath.removeAt(vertexClicked);
    this.polygon.setPath(newPath);
    this.fieldStore.setSelectedFieldArea(this.googleApi.maps.geometry.spherical.computeArea(this.polygon.getPath()));
    this.fieldStore.updateCurrentPolygon([
      this.polygon
        .getPath()
        .getArray()
        .map((o) => [o.lng(), o.lat()])
    ]);
  }

  addPolygonDragListener() {
    if (!this.polygon) return;

    if (this.polygonDragListenerHandle != null) {
      this.polygonDragListenerHandle.remove();
    }

    this.polygonDragListenerHandle = this.polygon.addListener('mouseup', () => {
      this.fieldStore.setSelectedFieldArea(this.findFieldArea());
      this.fieldStore.updateCurrentPolygon([
        this.polygon
          .getPath()
          .getArray()
          .map((o) => [o.lng(), o.lat()])
      ]);
    });
  }

  addMapClickListener() {
    if (this.mapClickListenerHandle != null) {
      this.mapClickListenerHandle.remove();
    }

    if (!this.map) return;
    this.mapClickListenerHandle = this.map.addListener('click', (e) => {
      if (!this.fieldStore.addFieldMode) return;

      if (!this.polygon) {
        this.createNewPolygon([]);
      }

      this.addPointToPath(e);
    });
  }

  addPointToPath(e: google.maps.MouseEvent) {
    const newPath = this.polygon.getPath();
    if (newPath == undefined || newPath.getLength() == 0) {
      this.polygon.setPath([e.latLng]);
    } else {
      newPath.push(e.latLng);
      this.polygon.setPath(newPath);
    }

    this.fieldStore.setSelectedFieldArea(this.findFieldArea());
    this.fieldStore.updateCurrentPolygon([
      this.polygon
        .getPath()
        .getArray()
        .map((o) => [o.lng(), o.lat()])
    ]);
  }

  /**
   * Returns the field area.
   */
  findFieldArea() {
    return this.polygon.getPath() == undefined ? 0 : this.googleApi.maps.geometry.spherical.computeArea(this.polygon.getPath());
  }
}
